/**
 * Report Settings Utility Functions
 *
 * Helper functions for managing report template ordering and settings
 */

import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import type { ReportSettings, OrderedReportTemplate } from './types'

/**
 * sort_key 가 유효한 숫자인지 확인.
 * 서버 블롭에는 프롬프트만 저장된 엔트리(sort_key 없음)나 과거에 잘못 저장된
 * sort_key: null 이 섞일 수 있다 — 이 값이 연산에 섞이면 NaN이 되고,
 * JSON.stringify(NaN) → null 로 직렬화되어 서버 500의 원인이 된다.
 */
function hasValidSortKey(setting: { sort_key?: unknown } | undefined): boolean {
  return !!setting && typeof setting.sort_key === 'number' && Number.isFinite(setting.sort_key)
}

/** 정렬 비교용 sort_key — 유효하지 않으면 맨 뒤(999) */
function comparableSortKey(setting: { sort_key?: unknown } | undefined): number {
  return hasValidSortKey(setting) ? (setting as { sort_key: number }).sort_key : 999
}

/**
 * Generate initial report settings from template list
 * All templates start as included (ignored: false) with sequential sort_key
 */
export function generateInitialSettings(
  templates: ReportTemplateDetailResponse[]
): ReportSettings {
  return templates.reduce((acc, template, index) => ({
    ...acc,
    [template.key]: {
      sort_key: index,
      ignored: false
    }
  }), {} as ReportSettings)
}

/**
 * Convert array order to sort_key mapping
 */
export function generateSortKeys(orderedIds: string[]): Record<string, number> {
  return orderedIds.reduce((acc, id, index) => ({
    ...acc,
    [id]: index
  }), {})
}

/**
 * Sort templates by their sort_key in settings
 * Templates without settings are placed at the end with sort_key 999
 */
export function sortTemplatesBySettings(
  templates: ReportTemplateDetailResponse[],
  settings: ReportSettings
): ReportTemplateDetailResponse[] {
  return [...templates].sort((a, b) => {
    return comparableSortKey(settings[a.key]) - comparableSortKey(settings[b.key])
  })
}

/**
 * Get ordered templates with their settings
 */
export function getOrderedTemplatesWithSettings(
  templates: ReportTemplateDetailResponse[],
  settings: ReportSettings
): OrderedReportTemplate[] {
  const sortedTemplates = sortTemplatesBySettings(templates, settings)

  return sortedTemplates.map(template => ({
    template,
    setting: settings[template.key] || { sort_key: 999, ignored: false }
  }))
}

/**
 * Toggle ignored status for a template
 */
export function toggleReportIgnored(
  settings: ReportSettings,
  templateId: string
): ReportSettings {
  const currentSetting = settings[templateId] || { sort_key: 999, ignored: false }

  return {
    ...settings,
    [templateId]: {
      ...currentSetting,
      ignored: !currentSetting.ignored
    }
  }
}

/**
 * Update sort_key values after drag-and-drop reordering
 */
export function updateSortKeysAfterDrag(
  settings: ReportSettings,
  draggedId: string,
  newIndex: number
): ReportSettings {
  // 1. Get current ordered list of template IDs
  const ordered = Object.entries(settings)
    .sort(([, a], [, b]) => comparableSortKey(a) - comparableSortKey(b))
    .map(([id]) => id)

  // 2. Find and remove dragged item from its old position
  const oldIndex = ordered.indexOf(draggedId)
  if (oldIndex === -1) {
    console.warn(`Template ${draggedId} not found in settings`)
    return settings
  }
  ordered.splice(oldIndex, 1)

  // 3. Insert at new position
  ordered.splice(newIndex, 0, draggedId)

  // 4. Regenerate sort_key values for all items
  return ordered.reduce((acc, id, index) => ({
    ...acc,
    [id]: {
      ...settings[id],
      sort_key: index
    }
  }), {} as ReportSettings)
}

/**
 * Merge existing settings with new templates
 * Preserves existing settings and adds new templates at the end
 */
export function mergeSettingsWithTemplates(
  existingSettings: ReportSettings,
  templates: ReportTemplateDetailResponse[]
): ReportSettings {
  const templateKeys = new Set(templates.map(t => t.key))

  // Remove settings for templates that no longer exist
  const validSettings = Object.entries(existingSettings)
    .filter(([key]) => templateKeys.has(key))
    .reduce((acc, [key, setting]) => ({
      ...acc,
      [key]: setting
    }), {} as ReportSettings)

  // Find max sort_key in existing settings
  // (유효한 숫자만 집계 — sort_key 없는 엔트리가 섞이면 Math.max가 NaN이 되어
  //  이후 모든 신규 sort_key가 NaN → JSON 직렬화 시 null 로 전송되는 버그 방지)
  const maxSortKey = Object.values(validSettings).reduce(
    (max, setting) => (hasValidSortKey(setting) ? Math.max(max, setting.sort_key) : max),
    -1
  )

  // Add new templates that don't have settings yet.
  // 기존 엔트리라도 유효한 sort_key 가 없으면(프롬프트만 저장된 엔트리, 과거의 null 등)
  // 새 템플릿과 동일하게 맨 뒤 번호를 배정해 정상 숫자로 복구한다 (기존 프롬프트 등 다른 키는 보존).
  let nextSortKey = maxSortKey + 1
  templates.forEach(template => {
    const existing = validSettings[template.key]
    if (!existing) {
      validSettings[template.key] = {
        sort_key: nextSortKey++,
        ignored: false
      }
    } else if (!hasValidSortKey(existing)) {
      validSettings[template.key] = {
        ...existing,
        sort_key: nextSortKey++,
        ignored: typeof existing.ignored === 'boolean' ? existing.ignored : false
      }
    }
  })

  return validSettings
}

/**
 * 저장 직전 정화 — sort_key 가 유효한 숫자가 아니면 키 자체를 제거하고
 * (null 전송 시 서버가 검증 없이 저장해 이후 500 발생), ignored 도 boolean 이 아니면 제거한다.
 * 키가 아예 없는 것은 서버에서 허용되므로 안전하다. 프롬프트 등 다른 키는 그대로 보존.
 */
export function sanitizeReportSettingsForSave(settings: ReportSettings): ReportSettings {
  const result: Record<string, Record<string, unknown>> = {}
  for (const [key, setting] of Object.entries(settings)) {
    if (!setting || typeof setting !== 'object') continue
    const entry: Record<string, unknown> = { ...setting }
    if (!hasValidSortKey(setting)) {
      delete entry.sort_key
    }
    if (typeof entry.ignored !== 'boolean') {
      delete entry.ignored
    }
    result[key] = entry
  }
  return result as unknown as ReportSettings
}

/**
 * Validate report settings structure
 */
export function validateReportSettings(settings: ReportSettings): boolean {
  try {
    // Check if settings is an object
    if (typeof settings !== 'object' || settings === null) {
      return false
    }

    // Validate each setting
    for (const [id, setting] of Object.entries(settings)) {
      if (!id || typeof id !== 'string') {
        return false
      }

      if (typeof setting !== 'object' || setting === null) {
        return false
      }

      if (typeof setting.sort_key !== 'number' || setting.sort_key < 0) {
        return false
      }

      if (typeof setting.ignored !== 'boolean') {
        return false
      }
    }

    return true
  } catch (error) {
    console.error('Error validating report settings:', error)
    return false
  }
}
